// File_name: src/App.tsx

import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { setNavigate } from '@/lib/navigation';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { useWebVitals } from '@/hooks/useWebVitals';
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode';
import { lazy, Suspense, useEffect } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { resolveHydrationWatchdog } from '@/lib/storage/StorageGuard';
import { initPixels } from '@/lib/tracking/pixels';
import { usePageTracking } from '@/lib/tracking/usePageTracking';
import ABThemeTestRunner from '@/components/ABThemeTestRunner';

// Critical page loaded immediately (landing page only)
import Index from './pages/Index';
import Maintenance from './pages/Maintenance';
import AliasRedirect from '@/components/seo/AliasRedirect';

// Helper for resilient lazy loading with retry and reload fallback
const lazyWithRetry = (
  importFn: () => Promise<{ default: React.ComponentType<object> }>
) => {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (error) {
      // Check if it's a chunk loading error
      if (
        error instanceof Error &&
        error.message.includes('Failed to fetch dynamically imported module')
      ) {
        // Clear any cached modules and reload the page
        console.warn('Chunk loading failed, reloading page...');
        window.location.reload();
        // Return a placeholder while reloading
        return { default: () => null };
      }
      throw error;
    }
  });
};

// Products pages lazy-loaded (not needed for initial landing page render)
const Products = lazyWithRetry(() => import('./pages/Products'));
const ProductDetail = lazyWithRetry(() => import('./pages/ProductDetail'));

// Non-critical pages lazy loaded with retry logic
const About = lazyWithRetry(() => import('./pages/About'));
const Blog = lazyWithRetry(() => import('./pages/Blog'));
const BlogPost = lazyWithRetry(() => import('./pages/BlogPost'));
const CGV = lazyWithRetry(() => import('./pages/CGV'));
const Cart = lazyWithRetry(() => import('./pages/Cart'));
const Checkout = lazyWithRetry(() => import('./pages/Checkout'));
const Contact = lazyWithRetry(() => import('./pages/Contact'));
const OrderConfirmationEntry = lazyWithRetry(
  () => import('./pages/OrderConfirmationEntry')
);
const OrderConfirmation = lazyWithRetry(
  () => import('./pages/OrderConfirmation')
);
const Invoice = lazyWithRetry(() => import('./pages/Invoice'));
// Legacy route — kept for backward-compatible redirect
const PaymentSuccess = lazyWithRetry(() => import('./pages/PaymentSuccess'));
const Wishlist = lazyWithRetry(() => import('./pages/Wishlist'));
const FAQ = lazyWithRetry(() => import('./pages/FAQ'));
const Auth = lazyWithRetry(() => import('./pages/Auth'));
const EnhancedProfile = lazyWithRetry(() => import('./pages/EnhancedProfile'));
const OrderHistory = lazyWithRetry(() => import('./pages/OrderHistory'));
const Returns = lazyWithRetry(() => import('./pages/Returns'));
const Shipping = lazyWithRetry(() => import('./pages/Shipping'));
const Story = lazyWithRetry(() => import('./pages/Story'));
const Artisans = lazyWithRetry(() => import('./pages/Artisans'));
const CareGuide = lazyWithRetry(() => import('./pages/CareGuide'));
const CareGuideBags = lazyWithRetry(() => import('./pages/CareGuideBags'));
const StyleGuideBags = lazyWithRetry(() => import('./pages/StyleGuideBags'));
const Terms = lazyWithRetry(() => import('./pages/Terms'));
const TermsOfService = lazyWithRetry(() => import('./pages/TermsOfService'));
const NotFound = lazyWithRetry(() => import('./pages/NotFound'));
const Unsubscribe = lazyWithRetry(() => import('./pages/Unsubscribe'));
const Logout = lazyWithRetry(() => import('./pages/Logout'));
const NewsletterExitIntent = lazyWithRetry(
  () => import('./components/NewsletterExitIntent')
);
const CompareProducts = lazyWithRetry(() => import('./pages/CompareProducts'));

// Essential context providers (Auth only - Cart/Wishlist/Currency/Theme migrated to Zustand)
import { AuthProvider } from '@/context/AuthContext';
// All other providers replaced by Zustand stores initialized in main.tsx
import ErrorBoundary from './components/ErrorBoundary';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
const TTIOptimizer = lazy(
  () => import('@/components/performance/TTIOptimizer')
);

// PWA Components - lazy loaded since not critical for initial render
const PWAInstallPrompt = lazy(() =>
  import('@/components/ui/PWAInstallPrompt').then((m) => ({
    default: m.PWAInstallPrompt,
  }))
);
const OfflineManager = lazy(() =>
  import('@/components/ui/OfflineManager').then((m) => ({
    default: m.OfflineManager,
  }))
);
const PushNotificationManager = lazy(() =>
  import('@/components/ui/PushNotificationManager').then((m) => ({
    default: m.PushNotificationManager,
  }))
);

// Navigation (persistent across routes)
import Navigation from '@/components/Navigation';

// Admin imports - lazy loaded since admin pages are rarely accessed
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const ProtectedAdminRoute = lazy(() =>
  import('./components/ProtectedAdminRoute').then((m) => ({
    default: m.ProtectedAdminRoute,
  }))
);
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
// AdminOrders (legacy) and AdminProductCatalog (read-only viewer) were removed
// during admin consolidation. See .lovable/plan.md for details.
// /admin/orders now uses AdminOrdersEnhanced; /admin/catalog redirects to /admin/products.
const AdminOrders = lazy(() => import('./pages/admin/AdminOrdersEnhanced'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminCustomers = lazy(() => import('./pages/admin/AdminCustomers'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminInventory = lazy(() => import('./pages/admin/AdminInventory'));
const AdminMarketing = lazy(() => import('./pages/admin/AdminMarketing'));
const AdminHeroImage = lazy(() => import('./pages/admin/AdminHeroImage'));
const AdminErrorReports = lazy(() => import('./pages/admin/AdminErrorReports'));
const AdminPromoCodes = lazy(() => import('./pages/admin/AdminPromoCodes'));
const AdminEmailTesting = lazy(() => import('./pages/admin/AdminEmailTesting'));
const AdminReviews = lazy(() =>
  import('./pages/admin/AdminReviews').then((m) => ({
    default: m.AdminReviews,
  }))
);
const AdminApiStatus = lazy(() => import('./pages/admin/AdminApiStatus'));
const AdminTranslations = lazy(() => import('./pages/admin/AdminTranslations'));
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'));
const AdminTags = lazy(() => import('./pages/admin/AdminTags'));
const AdminNewsletter = lazy(() => import('./pages/admin/AdminNewsletter'));

// Loading fallback component
const PageLoadingFallback = () => (
  <div className="min-h-dvh bg-background p-4">
    <div className="container mx-auto space-y-4">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-64 w-full" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    </div>
  </div>
);

const basePath: string = '/';

// Register global navigate function + tracking pixels
const NavigateRegistrar = () => {
  const nav = useNavigate();
  useEffect(() => {
    setNavigate(nav);
  }, [nav]);
  usePageTracking();
  return null;
};

// Scroll to top on every route change
const ScrollRestoration = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
};

// Wrapper component to handle maintenance mode check inside Router context
const MaintenanceWrapper = ({ children }: { children: React.ReactNode }) => {
  const { isMaintenanceMode } = useMaintenanceMode();
  const location = useLocation();

  // Allow admin routes even in maintenance mode
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Don't block rendering while checking maintenance mode — show content immediately
  // This prevents blank screens caused by slow/failing Supabase queries

  // If in maintenance mode and not an admin route, show maintenance page WITHOUT navigation
  if (isMaintenanceMode && !isAdminRoute) {
    return <Maintenance />;
  }

  // Admin routes should NOT show the main navigation
  if (isAdminRoute) {
    return <>{children}</>;
  }

  // Normal operation: show navigation + A/B test runner + children
  return (
    <>
      <Navigation />
      <ABThemeTestRunner />
      {children}
    </>
  );
};

const App = () => {
  // Initialize Web Vitals tracking
  useWebVitals();

  // Resolve hydration watchdog — app rendered successfully
  useEffect(() => {
    resolveHydrationWatchdog();
    initPixels();
  }, []);

  // Product prefetch removed — the Index page's useProductsWithTranslations()
  // hook is the single owner of the ['products', locale] query. The old
  // requestIdleCallback prefetch raced with the real query, wasting a
  // connection slot on the 6-per-host Chrome limit.

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<div className="min-h-dvh bg-background" />}>
          <OfflineManager>
            <AuthProvider>
              <TooltipProvider delayDuration={300}>
                <BrowserRouter
                  basename={basePath}
                  future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true,
                  }}
                >
                  <NavigateRegistrar />
                  <ScrollRestoration />
                  <MaintenanceWrapper>
                    <Suspense fallback={null}>
                      <PushNotificationManager />
                      <PWAInstallPrompt />
                    </Suspense>

                    <Routes>
                      {/* Critical route loaded immediately */}
                      <Route path="/" element={<Index />} />
                      {/* Common alias — landing is `/`, not `/home` (paths are case-sensitive) */}
                      <Route
                        path="/home"
                        element={<AliasRedirect to="/" />}
                      />
                      <Route
                        path="/Home"
                        element={<AliasRedirect to="/" />}
                      />
                      <Route
                        path="/HOME"
                        element={<AliasRedirect to="/" />}
                      />
                      <Route
                        path="/products"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <Products />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/shop"
                        element={<AliasRedirect to="/products" />}
                      />
                      <Route
                        path="/products/:id"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <ProductDetail />
                          </Suspense>
                        }
                      />

                      {/* Non-critical routes with lazy loading */}
                      <Route
                        path="/auth"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <Auth />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <EnhancedProfile />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/enhanced-profile"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <EnhancedProfile />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/orders"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <OrderHistory />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/cart"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <Cart />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/checkout"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <Checkout />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/order-confirmation"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <OrderConfirmationEntry />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/invoice/:orderId"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <Invoice />
                          </Suspense>
                        }
                      />
                      {/* Legacy route: redirect old links to new route */}
                      <Route
                        path="/payment-success"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <PaymentSuccess />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/order-confirmation/:orderReference"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <OrderConfirmation />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/compare"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CompareProducts />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/wishlist"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <Wishlist />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/blog"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <Blog />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/blog/:id"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <BlogPost />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/contact"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <Contact />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/shipping"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <Shipping />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/returns"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <Returns />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/faq"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <FAQ />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/about"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <About />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/terms"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <Terms />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/cgv"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CGV />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/terms-of-service"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <TermsOfService />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/unsubscribe"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <Unsubscribe />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/logout"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <Logout />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/story"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <Story />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/artisans"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <Artisans />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/care-guide"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CareGuide />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/care-guide/straw-bags"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <CareGuideBags />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/blog/how-to-style-straw-bags"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <StyleGuideBags />
                          </Suspense>
                        }
                      />

                      {/* Admin routes with lazy loading */}
                      <Route
                        path="/admin/login"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <AdminLogin />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/admin"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <ProtectedAdminRoute>
                              <AdminLayout />
                            </ProtectedAdminRoute>
                          </Suspense>
                        }
                      >
                        <Route
                          index
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminDashboard />
                            </Suspense>
                          }
                        />
                        <Route
                          path="dashboard"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminDashboard />
                            </Suspense>
                          }
                        />
                        <Route
                          path="products"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminProducts />
                            </Suspense>
                          }
                        />
                        <Route
                          path="hero-image"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminHeroImage />
                            </Suspense>
                          }
                        />
                        <Route
                          path="inventory"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminInventory />
                            </Suspense>
                          }
                        />
                        <Route
                          path="orders"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminOrders />
                            </Suspense>
                          }
                        />
                        <Route
                          path="orders-enhanced"
                          element={<Navigate to="/admin/orders" replace />}
                        />

                        <Route
                          path="customers"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminCustomers />
                            </Suspense>
                          }
                        />
                        <Route
                          path="marketing"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminMarketing />
                            </Suspense>
                          }
                        />
                        <Route
                          path="promo-codes"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminPromoCodes />
                            </Suspense>
                          }
                        />
                        <Route
                          path="analytics"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminAnalytics />
                            </Suspense>
                          }
                        />
                        <Route
                          path="error-reports"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminErrorReports />
                            </Suspense>
                          }
                        />
                        <Route
                          path="email-testing"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminEmailTesting />
                            </Suspense>
                          }
                        />
                        <Route
                          path="reviews"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminReviews />
                            </Suspense>
                          }
                        />
                        <Route
                          path="api-status"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminApiStatus />
                            </Suspense>
                          }
                        />
                        <Route
                          path="catalog"
                          element={<Navigate to="/admin/products" replace />}
                        />

                        <Route
                          path="blog"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminBlog />
                            </Suspense>
                          }
                        />
                        <Route
                          path="translations"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminTranslations />
                            </Suspense>
                          }
                        />
                        <Route
                          path="tags"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminTags />
                            </Suspense>
                          }
                        />
                        <Route
                          path="newsletter"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminNewsletter />
                            </Suspense>
                          }
                        />
                        <Route
                          path="settings"
                          element={
                            <Suspense fallback={<PageLoadingFallback />}>
                              <AdminSettings />
                            </Suspense>
                          }
                        />
                      </Route>

                      {/* Catch-all route for 404 pages */}
                      <Route
                        path="*"
                        element={
                          <Suspense fallback={<PageLoadingFallback />}>
                            <NotFound />
                          </Suspense>
                        }
                      />
                    </Routes>
                  </MaintenanceWrapper>

                  {/* Inside Router so exit-intent can follow pathname (suppress on /checkout, etc.) */}
                  <Suspense fallback={null}>
                    <NewsletterExitIntent />
                  </Suspense>
                </BrowserRouter>

                {/* Système de notifications */}
                <Toaster />
                <Sonner richColors expand visibleToasts={3} />

                {/* TTI Optimizer - lazy loaded, runs after everything else */}
                <Suspense fallback={null}>
                  <TTIOptimizer />
                </Suspense>

                {/* Devtools: omit in E2E (VITE_E2E) and when Cypress runs the app — avoids floating control vs. selectors */}
                {import.meta.env.DEV &&
                  import.meta.env.VITE_E2E !== '1' &&
                  typeof window !== 'undefined' &&
                  !(window as unknown as { Cypress?: unknown }).Cypress && (
                    <ReactQueryDevtools initialIsOpen={false} />
                  )}
              </TooltipProvider>
            </AuthProvider>
          </OfflineManager>
        </Suspense>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
